/**
 * Session settings: what the player picks on the setup screen. Resolved from the URL (deep
 * links, bookmarks), then the last session in localStorage, then defaults. No DOM here.
 */
import { TIMES_OF_DAY, WEATHERS, type RealismLevel, type TimeOfDay, type Weather } from '@atc/core';
import { AIRPORTS } from './airports';
import { SCENARIOS, type ScenarioId } from './scenarios';

/** radar = scope only; split = scope and tower view side by side; tower = tower view only; pip = tower view as a panel over the scope. */
export type Layout = 'radar' | 'tower' | 'split' | 'pip';
export type Traffic = 'light' | 'normal' | 'busy';
export type Mode = 'free' | 'shift' | 'scenario';
export type ShiftMinutes = 15 | 30 | 60;

export interface SessionSettings {
  /** Controller responsibility, independent of display layout. Missing old settings use legacy. */
  position?: 'legacy' | 'tower';
  coaching?: boolean;
  radioPace: 'normal' | 'brisk';
  languageMode: 'offline' | 'hybrid' | 'model';
  languageProvider: 'auto' | 'local' | 'hosted';
  /** Who a callsign-free transmission goes to: nobody, the selected aircraft, or the one you were just talking to. */
  addressing: 'explicit' | 'selected' | 'conversation';
  pilotInitiative: 'essential' | 'normal';
  shortCallsigns: boolean;
  languageBudget: number;
  voiceInterpretation: 'transcript' | 'direct';
  /** Retained on retry so generated traffic is repeatable. */
  seed?: number;
  speechInput: boolean;
  scenario: ScenarioId;
  graphicsQuality: 'auto' | 0 | 1 | 2 | 3;
  assists: boolean;
  /** Carrier hiss and squelch under the radio voices. */
  radioNoise: 'off' | 'low' | 'normal';
  /** Synthesized voices speak with the crew's (or the controller's) accent; off keeps neutral English. */
  radioAccents: boolean;
  /** Show the opening brief (runways, procedures, wind) before the frequency opens. */
  atis: boolean;
  radioProvider: 'browser' | 'openai';
  /** Retired: spoken clearances now execute and the pilot reads back. Kept so saved settings still parse. */
  voiceImmediate: boolean;
  /** Also play the controller's own spoken transmissions back through the radio voice. Off: you already said them. */
  echoSpeech: boolean;
  /** Lower-case ICAO code. */
  airport: string;
  /** Runway ends in use for generated arrivals and departures. Empty/invalid lists fall back to the airport defaults. */
  arrivalRunways: string[];
  departureRunways: string[];
  realism: RealismLevel;
  voice: boolean;
  layout: Layout;
  mode: Mode;
  traffic: Traffic;
  shiftMinutes: ShiftMinutes;
  /** Split layout: share of the width given to the radar scope, 0.2-0.8. */
  split: number;
  /** Strips and radio panel shown. */
  side: boolean;
  /** Go full screen when a session starts from the setup screen. */
  fullscreen: boolean;
  /** Alert tones for traffic alerts, separation losses, landings and handoffs. */
  sound: boolean;
  /** STARs and approaches on the scope: for the runways in use, all of them, or none. */
  procedures: 'off' | 'active' | 'all';
  /** The selected aircraft's flown track and route ahead. */
  trail: boolean;
  /** How often a crew declares an emergency; fuel is always tracked in free play. */
  emergencies: 'off' | 'rare' | 'normal';
  /** When the session starts; the clock then runs with sim time. "now" is solar time at the airport. */
  timeOfDay: TimeOfDay;
  /** Sky, visibility and wind strength for the session (core/src/conditions.ts). */
  weather: Weather;
  /** Who chooses what the pilots say: the sim's own rules, or a model the sim checks. */
  pilotBrain: 'auto' | 'rules' | 'llm';
  brainProvider: 'auto' | 'local' | 'hosted';
  /** Stop asking the model once this session's estimate passes this many dollars. */
  brainSpendCapUsd: number;
}

/** Server-owned operational choices. They are deliberately absent from player URLs/storage. */
export type ManagedGameConfig = Pick<SessionSettings, 'radioPace' | 'languageMode' | 'languageProvider' | 'addressing' | 'pilotInitiative' | 'shortCallsigns' | 'languageBudget' | 'voiceInterpretation' | 'pilotBrain' | 'brainProvider' | 'brainSpendCapUsd' | 'radioProvider'> & {
  /** Dollars of model spend a day, everyone together. The environment sets the ceiling; this can only lower it. Zero leaves it to the environment. */
  dailyBudgetUsd: number;
  /** Dollars of model spend a day for one player, at or below the environment's ceiling. Admin only. */
  playerBudgetUsd?: number;
  schemaVersion: number;
  allowVoicePlayback: boolean;
  allowSpeechInput: boolean;
  telemetryEnabled: boolean;
  feedbackEnabled: boolean;
  /** Minutes without controller input before a game pauses itself; zero turns it off. */
  idlePauseMinutes: number;
  /** Dollars one game may spend on hosted AI, enforced by the service. Admin only: never sent to players. */
  sessionBudgetUsd?: number;
  /** How generated voices sound; the service turns these keys into its own prompt text (voice/speech.mjs). */
  radioVoicePool: 'radio' | 'wide';
  radioControllerVoice: string;
  radioAccentStrength: 'off' | 'light' | 'strong';
  radioCharacter: 'uniform' | 'crew';
  updatedAt?: string;
};

export const DEFAULT_SETTINGS: SessionSettings = { radioPace: 'brisk', languageMode: 'model', languageProvider: 'auto', addressing: 'selected', pilotInitiative: 'normal', shortCallsigns: true, languageBudget: 100, voiceInterpretation: 'transcript', speechInput: true, scenario: 'arrival-sequence', graphicsQuality: 3, assists: true, atis: true, radioNoise: 'low', radioAccents: true, radioProvider: 'openai', voiceImmediate: false, echoSpeech: false, airport: 'ksfo', arrivalRunways: ['28R'], departureRunways: ['28L'], realism: 'mid', voice: true, layout: 'split', mode: 'free', traffic: 'normal', shiftMinutes: 30, split: 0.5, side: true, fullscreen: true, sound: true, procedures: 'active', trail: true, emergencies: 'rare', timeOfDay: 'now', weather: 'scattered', pilotBrain: 'auto', brainProvider: 'auto', brainSpendCapUsd: 1 };
export const DEFAULT_GAME_CONFIG: ManagedGameConfig = { schemaVersion: 2, dailyBudgetUsd: 0, playerBudgetUsd: 0, radioPace: 'brisk', languageMode: 'model', languageProvider: 'auto', addressing: 'selected', pilotInitiative: 'normal', shortCallsigns: true, languageBudget: 100, voiceInterpretation: 'transcript', pilotBrain: 'auto', brainProvider: 'auto', brainSpendCapUsd: 1, radioProvider: 'openai', radioVoicePool: 'radio', radioControllerVoice: 'ash', radioAccentStrength: 'light', radioCharacter: 'crew', allowVoicePlayback: true, allowSpeechInput: true, telemetryEnabled: true, feedbackEnabled: true, idlePauseMinutes: 3, sessionBudgetUsd: 1 };

/**
 * Assumption: spawn rates per hour for the traffic presets; "normal" is the sim default.
 * `initial*` is what is already on frequency when the session opens: one of each is enough to
 * learn on, and the sim staggers their check-ins so they do not talk over each other.
 */
export const TRAFFIC: Record<Traffic, { arrivalsPerHour: number; departuresPerHour: number; initialArrivals: number; initialDepartures: number }> = {
  light: { arrivalsPerHour: 12, departuresPerHour: 8, initialArrivals: 1, initialDepartures: 1 },
  normal: { arrivalsPerHour: 24, departuresPerHour: 18, initialArrivals: 1, initialDepartures: 1 },
  busy: { arrivalsPerHour: 36, departuresPerHour: 28, initialArrivals: 2, initialDepartures: 1 },
};

export const STORAGE_KEY = 'atc.settings.v2';
const LEGACY_STORAGE_KEY = 'atc.settings.v1';
/**
 * Bumped when a default changes enough that saved settings should follow it once. 3: realism
 * moved from Low to Mid and push to talk became on (it connects only on the first press).
 * Saved settings wrote every field, defaults included, so without this nobody who had played
 * before would ever see the new defaults. 4: the display defaults to Split and push to talk
 * is reset on for existing profiles; choices saved after this migration are preserved. 5: the
 * radio is heard by default; saves wrote voice:false as the old default, so it is dropped once.
 */
export const SETTINGS_VERSION = 5;

function migrate(raw: Record<string, unknown>): Record<string, unknown> {
  const version = typeof raw.settingsVersion === 'number' ? raw.settingsVersion : 2;
  if (version >= SETTINGS_VERSION) return raw;
  // Dropped rather than rewritten, so the current defaults fill them in.
  const rest = { ...raw };
  if (version < 3) { delete rest.speechInput; if (rest.realism === 'low') delete rest.realism; }
  if (version < 4) { delete rest.speechInput; delete rest.layout; }
  if (version < 5) delete rest.voice;
  return rest;
}

const LEVELS: readonly RealismLevel[] = ['low', 'mid', 'high'];
const LAYOUTS: readonly Layout[] = ['radar', 'tower', 'split', 'pip'];
const TRAFFICS: readonly Traffic[] = ['light', 'normal', 'busy'];
const MODES: readonly Mode[] = ['free', 'shift', 'scenario'];

function isOneOf<T extends string>(v: string | undefined, list: readonly T[]): v is T {
  return v !== undefined && (list as readonly string[]).includes(v);
}

/** Keep only valid fields; anything else (old, corrupt or hand-edited data) is dropped. */
export function sanitize(raw: unknown): Partial<SessionSettings> {
  if (!raw || typeof raw !== 'object') return {};
  const r = raw as Record<string, unknown>;
  const lower = (v: unknown) => (typeof v === 'string' ? v.toLowerCase() : undefined);
  const out: Partial<SessionSettings> = {};
  if (r.position === 'legacy' || r.position === 'tower') out.position = r.position;
  if (typeof r.coaching === 'boolean') out.coaching = r.coaching;
  const runways = (v: unknown) => Array.isArray(v) ? [...new Set(v.filter((x): x is string => typeof x === 'string' && /^\d{1,2}[LRC]?$/.test(x.toUpperCase())).map((x) => x.toUpperCase()))] : undefined;
  if (typeof r.seed === 'number' && Number.isSafeInteger(r.seed) && r.seed >= 0 && r.seed <= 2147483647) out.seed = r.seed;
  if (typeof r.speechInput === 'boolean') out.speechInput = r.speechInput;
  if (SCENARIOS.some((s) => s.id === r.scenario)) out.scenario = r.scenario as ScenarioId;
  if (r.graphicsQuality === 'auto' || [0, 1, 2, 3].includes(r.graphicsQuality as number)) out.graphicsQuality = r.graphicsQuality as SessionSettings['graphicsQuality'];
  if (typeof r.assists === 'boolean') out.assists = r.assists;
  if (typeof r.atis === 'boolean') out.atis = r.atis;
  if (r.radioNoise === 'off' || r.radioNoise === 'low' || r.radioNoise === 'normal') out.radioNoise = r.radioNoise;
  if (typeof r.radioAccents === 'boolean') out.radioAccents = r.radioAccents;
  if (typeof r.voiceImmediate === 'boolean') out.voiceImmediate = r.voiceImmediate;
  if (typeof r.echoSpeech === 'boolean') out.echoSpeech = r.echoSpeech;
  const airport = lower(r.airport);
  if (airport && AIRPORTS.some((a) => a.icao === airport)) out.airport = airport;
  const arrivals = runways(r.arrivalRunways), departures = runways(r.departureRunways);
  if (arrivals?.length) out.arrivalRunways = arrivals;
  if (departures?.length) out.departureRunways = departures;
  const realism = lower(r.realism);
  if (isOneOf(realism, LEVELS)) out.realism = realism;
  const layout = lower(r.layout);
  if (isOneOf(layout, LAYOUTS)) out.layout = layout;
  const traffic = lower(r.traffic);
  if (isOneOf(traffic, TRAFFICS)) out.traffic = traffic;
  const mode = lower(r.mode);
  if (isOneOf(mode, MODES)) out.mode = mode;
  if (typeof r.voice === 'boolean') out.voice = r.voice;
  if (r.shiftMinutes === 15 || r.shiftMinutes === 30 || r.shiftMinutes === 60) out.shiftMinutes = r.shiftMinutes;
  if (typeof r.split === 'number' && r.split >= 0.2 && r.split <= 0.8) out.split = r.split;
  if (typeof r.side === 'boolean') out.side = r.side;
  if (typeof r.fullscreen === 'boolean') out.fullscreen = r.fullscreen;
  if (typeof r.sound === 'boolean') out.sound = r.sound;
  if (r.procedures === 'off' || r.procedures === 'active' || r.procedures === 'all') out.procedures = r.procedures;
  if (typeof r.trail === 'boolean') out.trail = r.trail;
  if (r.emergencies === 'off' || r.emergencies === 'rare' || r.emergencies === 'normal') out.emergencies = r.emergencies;
  const timeOfDay = lower(r.timeOfDay);
  if (isOneOf(timeOfDay, TIMES_OF_DAY)) out.timeOfDay = timeOfDay;
  const weather = lower(r.weather);
  if (isOneOf(weather, WEATHERS)) out.weather = weather;
  return out;
}

export function readFromUrl(params: URLSearchParams): Partial<SessionSettings> {
  const raw: Record<string, unknown> = {
    position: params.get('position') ?? undefined,
    ...(params.has('coaching') ? { coaching: params.get('coaching') !== '0' } : {}),
    airport: params.get('airport') ?? undefined,
    realism: params.get('realism') ?? undefined,
    layout: params.get('layout') ?? undefined,
    traffic: params.get('traffic') ?? undefined,
    mode: params.get('mode') ?? undefined,
    scenario: params.get('scenario') ?? undefined,
    procedures: params.get('procs') ?? undefined,
    emergencies: params.get('emergencies') ?? undefined,
    timeOfDay: params.get('tod') ?? undefined,
    weather: params.get('wx') ?? undefined,
    arrivalRunways: params.get('arrRunways')?.split(',') ?? undefined,
    departureRunways: params.get('depRunways')?.split(',') ?? undefined,
  };
  if (params.has('trail')) raw.trail = params.get('trail') === '1';
  if (params.has('quality')) raw.graphicsQuality = params.get('quality') === 'auto' ? 'auto' : Number(params.get('quality'));
  if (params.has('assists')) raw.assists = params.get('assists') === '1';
  if (params.has('atis')) raw.atis = params.get('atis') === '1';
  if (params.has('immediate')) raw.voiceImmediate = params.get('immediate') === '1';
  if (params.has('mic')) raw.speechInput = params.get('mic') === '1';
  if (params.has('seed')) raw.seed = Number(params.get('seed'));
  if (params.has('voice')) raw.voice = params.get('voice') === '1';
  if (params.has('shift')) raw.shiftMinutes = Number(params.get('shift'));
  // Before the menu existed, ?tower=1 opened the tower panel.
  if (!params.has('layout') && params.get('tower') === '1') raw.layout = 'pip';
  // A link that names an airport but no session type means free play, not the last session's shift.
  if (params.has('airport') && !params.has('mode')) raw.mode = 'free';
  return sanitize(raw);
}

export function toUrlParams(s: SessionSettings): URLSearchParams {
  const p = new URLSearchParams({ airport: s.airport, realism: s.realism, traffic: s.traffic, layout: s.layout, voice: s.voice ? '1' : '0' });
  if (s.position) p.set('position', s.position);
  if (s.coaching !== undefined) p.set('coaching', s.coaching ? '1' : '0');
  if (s.mode !== 'free') { p.set('mode', s.mode); p.set('shift', String(s.shiftMinutes)); }
  p.set('quality', String(s.graphicsQuality)); p.set('assists', s.assists ? '1' : '0'); p.set('atis', s.atis ? '1' : '0');
  p.set('immediate', s.voiceImmediate ? '1' : '0');
  p.set('scenario', s.scenario);
  p.set('arrRunways', s.arrivalRunways.join(',')); p.set('depRunways', s.departureRunways.join(','));
  p.set('mic', s.speechInput ? '1' : '0');
  p.set('procs', s.procedures); p.set('trail', s.trail ? '1' : '0'); p.set('emergencies', s.emergencies);
  p.set('tod', s.timeOfDay); p.set('wx', s.weather);
  if (s.seed !== undefined) p.set('seed', String(s.seed));
  return p;
}

/** A URL that names an airport starts the game directly. */
export const isDeepLink = (params: URLSearchParams): boolean => params.has('airport');

export function readFromStorage(storage: Pick<Storage, 'getItem'> | undefined): Partial<SessionSettings> {
  try {
    const text = storage?.getItem(STORAGE_KEY) ?? storage?.getItem(LEGACY_STORAGE_KEY);
    const raw: unknown = text ? JSON.parse(text) : null;
    return raw && typeof raw === 'object' ? sanitize(migrate(raw as Record<string, unknown>)) : {};
  } catch {
    return {};
  }
}

export function saveSettings(s: SessionSettings, storage: Pick<Storage, 'setItem'> | undefined): void {
  try { storage?.setItem(STORAGE_KEY, JSON.stringify({ ...sanitize(s), settingsVersion: SETTINGS_VERSION })); } catch { /* private mode or blocked storage: settings last for this page only */ }
}

export function resolveSettings(fromUrl: Partial<SessionSettings>, stored: Partial<SessionSettings>, managed: ManagedGameConfig = DEFAULT_GAME_CONFIG): SessionSettings {
  const { schemaVersion: _schemaVersion, allowVoicePlayback: _allowVoicePlayback, allowSpeechInput: _allowSpeechInput, telemetryEnabled: _telemetryEnabled, feedbackEnabled: _feedbackEnabled, updatedAt: _updatedAt, dailyBudgetUsd: _dailyBudgetUsd, playerBudgetUsd: _playerBudgetUsd, idlePauseMinutes: _idlePauseMinutes, sessionBudgetUsd: _sessionBudgetUsd, radioVoicePool: _pool, radioControllerVoice: _controllerVoice, radioAccentStrength: _accentStrength, radioCharacter: _character, ...sessionManaged } = managed;
  return { ...DEFAULT_SETTINGS, ...sessionManaged, ...stored, ...fromUrl };
}

export async function loadGameConfig(request: typeof fetch = fetch): Promise<ManagedGameConfig> {
  try {
    const response = await request('/api/game-config', { headers: { 'x-atc-client': '1' }, signal: AbortSignal.timeout(4000) });
    if (!response.ok) throw new Error();
    const raw = await response.json() as Partial<ManagedGameConfig>;
    return { ...DEFAULT_GAME_CONFIG, ...raw };
  } catch { return { ...DEFAULT_GAME_CONFIG, languageMode: 'offline', pilotBrain: 'rules', radioProvider: 'browser', telemetryEnabled: false, feedbackEnabled: false }; }
}

export function browserStorage(): Storage | undefined {
  try { return localStorage; } catch { return undefined; }
}
